import { z } from "zod";

/** OTA `List_VehCategory` codes, except `Other_`. Stored and sent exactly as listed. */
export const VEHICLE_TYPE_CODES = [
  "2WheelVehicle",
  "All-Terrain",
  "Boat",
  "Car/Sedan",
  "Coach/Bus",
  "CommercialVan/Truck",
  "Convertible",
  "Coupe",
  "Crossover",
  "ExtendedCabPickup",
  "Limo",
  "Monospace",
  "Motorcycle",
  "Motorhome",
  "Pickup",
  "Recreational",
  "RegularCabPickup",
  "Roadster",
  "Shuttle",
  "Special",
  "SpecialOffer",
  "Sport",
  "StationWagon",
  "SUV",
  "Train",
  "Truck",
  "Van",
] as const;

export type VehicleTypeCode = typeof VEHICLE_TYPE_CODES[number];

const VehicleTypeCodeSchema = z.enum(VEHICLE_TYPE_CODES);

export class VehicleTypeValidationError extends Error {
  constructor(public readonly value: unknown) {
    super(`Invalid vehicle type: ${String(value)}`);
    this.name = "VehicleTypeValidationError";
  }
}

/** Vehicle type of a Vehicle extra, required for it: one code of the closed OTA list. */
export class VehicleType {
  private static readonly instances: ReadonlyMap<VehicleTypeCode, VehicleType> = new Map(
    VEHICLE_TYPE_CODES.map(code => [code, new VehicleType(code)])
  );

  private constructor(public readonly code: VehicleTypeCode) {}

  static of(code: string): VehicleType {
    const parsed = VehicleTypeCodeSchema.safeParse(code);
    if (!parsed.success) {
      throw new VehicleTypeValidationError(code);
    }
    return VehicleType.instances.get(parsed.data)!;
  }

  /** `undefined` for an empty or unknown code, e.g. an unset proto wrapper. */
  static tryOf(code: string | undefined): VehicleType | undefined {
    return VehicleType.isValid(code) ? VehicleType.of(code) : undefined;
  }

  static isValid(code: string | undefined): code is VehicleTypeCode {
    return VehicleTypeCodeSchema.safeParse(code).success;
  }

  /** Every vehicle type, in the OTA list order. The dropdown sorts them by translated name. */
  static all(): readonly VehicleType[] {
    return Array.from(VehicleType.instances.values());
  }

  equals(other: VehicleType | undefined): boolean {
    return other !== undefined && other.code === this.code;
  }

  toString(): string {
    return this.code;
  }
}
